import React, { useEffect, useState } from 'react';
import MaterialTable, {
    MTableBody,
    MTableFilterRow,
    MTableToolbar,
} from '@material-table/core';
import axios from 'axios';
import { API_URL } from '../../../utils/setApiUrl.js';
import 'date-fns';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Paper } from '@material-ui/core';
import { Settings } from 'luxon';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import DescriptionIcon from '@material-ui/icons/Description';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WebAppConfigService from '../../../services/WebAppConfigService';
import { useTranslation } from 'react-i18next';
import { getAppTimezone, toTzDate } from '../../../utils/timezone';
import { getMaterialTableExportData } from '../../../utils/getMaterialTableExportData';
import {
    formatToolbarDateForApi,
    getDefaultToolbarEndDate,
    normalizeToolbarEndDate,
    normalizeToolbarStartDate,
} from '../../../utils/reportDateDefaults';
import { useDepartmentsOptions } from '../../../utils/useDepartmentsOptions';
import { reportDatePickerSlotProps } from '../../../utils/reportDatePickerSlotProps';

const useStyles = makeStyles(theme => ({
    button: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(2.5),
    },
    grid: {
        marginLeft: theme.spacing(2),
    },
    toolbar: { marginLeft: theme.spacing(10) },
    absoluteContainer: {
        position: 'absolute',
        zIndex: 9,
        width: '45vw',
        [theme.breakpoints.down('md')]: {
            position: 'initial',
        },
    },
}));

const WorkingHoursTable = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const departmentsOptions = useDepartmentsOptions();

    // Get user role from localStorage as a simple string
    const [isAnalyzer, setIsAnalyzer] = useState(false);

    // Initialize user role from localStorage when component mounts
    useEffect(() => {
        const userRole = localStorage.getItem('level');
        // Check if the role is 'analyzer'
        if (userRole) {
            setIsAnalyzer(userRole === 'analyzer');
        }
    }, []);

    useEffect(() => {
        retrieveSystemSettings();
    }, []);

    const [settings, setSettings] = useState([]);

    const retrieveSystemSettings = () => {
        WebAppConfigService.getAll()
            .then(response => {
                setSettings(response.data);
            })
            .catch(e => {
                console.log(e);
            });
    };

    const locData = JSON.parse(localStorage.getItem('loc'));

    const client = axios.create({
        baseURL: API_URL,
        timeout: 300000,
    });

    // ! Gap Filter Start

    var x = new Date();
    const [selectedStartDate, handleStartDateChange] = useState(null);

    const [selectedEndDate, handleEndDateChange] = useState(
        getDefaultToolbarEndDate
    );

    const [events, setEvent] = useState([]);

    const [filteredDay, setFilteredDay] = useState([]);
    const [dataFetched, setDataFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const retrieveFilteredDay = async () => {
        setIsLoading(true); // Start loading

        setFilteredDay([]);

        const formattedStartDate = formatToolbarDateForApi(selectedStartDate);
        const newEndDate = formatToolbarDateForApi(selectedEndDate);

        // Pre-format datetime fields in the configured app timezone so the
        // MaterialTable rendering path can never re-interpret them in the
        // browser's local timezone. We attach formatted display strings to
        // separate fields and keep the original ISO for sorting.
        const tz = getAppTimezone();
        const formatInAppTz = iso => {
            if (!iso || iso === 'null' || iso === 'empty') return '';
            const d = new Date(iso);
            if (isNaN(d.getTime())) return iso;
            const parts = new Intl.DateTimeFormat('en-GB', {
                timeZone: tz,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            }).formatToParts(d);
            const m = {};
            for (const p of parts) m[p.type] = p.value;
            if (m.hour === '24') m.hour = '00';
            const lang = i18n.language;
            if (lang === 'bg') {
                return `${m.day}.${m.month}.${m.year} г., ${m.hour}:${m.minute}:${m.second} ч.`;
            }
            if (lang === 'en') {
                return `${m.month}/${m.day}/${m.year} ${m.hour}:${m.minute}:${m.second}`;
            }
            return `${m.day}/${m.month}/${m.year} ${m.hour}:${m.minute}:${m.second}`;
        };
        const preformatRow = row => ({
            ...row,
            first_entry_time_display: formatInAppTz(row.first_entry_time),
            last_exit_time_display: formatInAppTz(row.last_exit_time),
        });

        if (Array.isArray(locData)) {
            const joinedValues = locData.join(',');
            const request = client.get(
                `/working-hours/date/${formattedStartDate}/${newEndDate}/loc/${joinedValues}`
            );
            try {
                const response = await request;
                if (response && response.data) {
                    setFilteredDay(response.data.map(preformatRow));
                    setDataFetched(true);
                } else {
                    throw new Error('No data found in response');
                }
                setOpen(false);
            } catch (error) {
                setIserror(error.response);
                setErrorMessages('Не бяха намарени отклонения!'); // You can handle the JSX part where you render this message
                console.error(error.response);
            } finally {
                setIsLoading(false); // End loading regardless of success or failure
            }
        } else if (locData.id === 'all') {
            const request = client.get(
                `/working-hours/date/${formattedStartDate}/${newEndDate}/all`
            );
            try {
                const response = await request;
                if (response && response.data) {
                    setFilteredDay(response.data.map(preformatRow));
                    setDataFetched(true);
                } else {
                    throw new Error('No data found in response');
                }
                setOpen(false);
            } catch (error) {
                setIserror(error.response);
                setErrorMessages('Не бяха намарени отклонения!'); // You can handle the JSX part where you render this message
                console.error(error.response);
            } finally {
                setIsLoading(false); // End loading regardless of success or failure
            }
        }
    };

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => {
        setOpen(true);
    };

    // * Add Filters to State Start
    const [selectedRows, setSelectedRows] = useState([]);

    // const [components] = useState({
    // 	Toolbar = props => <MTableToolbar {...props} />,
    // })

    // * Add Filters to State End

    const [components] = useState({
        Toolbar: props => (
            <div>
                <MTableToolbar {...props} />
            </div>
        ),
        FilterRow: props => <MTableFilterRow {...props} />,
    });

    // Extend dayjs with plugins
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Function to get date format and locale based on current language
    const getDateFormatAndLocale = () => {
        const language = i18n.language;
        switch (language) {
            case 'en':
                return {
                    format: 'MM/DD/YYYY HH:mm:ss',
                    locale: 'en',
                    dateFormatExport: date => {
                        return `${date.getMonth() +
                            1}/${date.getDate()}/${date.getFullYear()}, ${date
                                .getHours()
                                .toString()
                                .padStart(2, '0')}:${date
                                    .getMinutes()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getSeconds()
                                        .toString()
                                        .padStart(2, '0')}`;
                    },
                };
            case 'es':
                return {
                    format: 'DD/MM/YYYY HH:mm:ss',
                    locale: 'es',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                                .toString()
                                .padStart(2, '0')}/${date.getFullYear()}, ${date
                                    .getHours()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getSeconds()
                                            .toString()
                                            .padStart(2, '0')}`;
                    },
                };
            case 'bg':
            default:
                return {
                    format: 'DD.MM.YYYY г., HH:mm:ss ч.',
                    locale: 'bg',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                                .toString()
                                .padStart(2, '0')}.${date.getFullYear()} г., ${date
                                    .getHours()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getSeconds()
                                            .toString()
                                            .padStart(2, '0')} ч.`;
                    },
                };
        }
    };

    // Get the current date format configuration
    const [dateConfig, setDateConfig] = useState(getDateFormatAndLocale());

    // Update date config when language changes
    useEffect(() => {
        setDateConfig(getDateFormatAndLocale());
    }, [i18n.language]);

    // ! Define table columns for export
    // Columns state that will update when language changes
    const [columns, setColumns] = useState([]);

    // Update columns when language changes
    useEffect(() => {
        setColumns([
            {
                title: t('reports.workingHoursReport.employeeID'),
                field: 'user_id',
                editable: 'never',
            },
            {
                title: t('reports.workingHoursReport.employeeName'),
                field: 'user_name',
                editable: 'never',
            },
            {
                title: t('reports.workingHoursReport.deviceLocation'),
                field: 'loc_name',
                editable: 'never',
            },
            {
                title: t('reports.workingHoursReport.department'),
                field: 'sub_loc_name',
                lookup: departmentsOptions,
                editable: 'never',
            },
            {
                title: t('reports.workingHoursReport.date'),
                field: 'date',
                type: 'date',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                editable: 'never',
                filtering: false,
                render: rowData => {
                    if (
                        rowData.date &&
                        rowData.date !== 'null' &&
                        rowData.date !== 'empty'
                    ) {
                        if (i18n.language === 'bg') {
                            // Custom formatting for Bulgarian dates with Cyrillic characters
                            const date = toTzDate(rowData.date);
                            return `${date
                                .getDate()
                                .toString()
                                .padStart(2, '0')}.${(date.getMonth() + 1)
                                    .toString()
                                    .padStart(2, '0')}.${date.getFullYear()} г.`;
                        } else if (i18n.language === 'en') {
                            // US format: MM/DD/YYYY
                            const date = toTzDate(rowData.date);
                            return `${(date.getMonth() + 1)
                                .toString()
                                .padStart(2, '0')}/${date
                                    .getDate()
                                    .toString()
                                    .padStart(2, '0')}/${date.getFullYear()}`;
                        } else if (i18n.language === 'es') {
                            // Spanish format: DD/MM/YYYY
                            const date = toTzDate(rowData.date);
                            return `${date
                                .getDate()
                                .toString()
                                .padStart(2, '0')}/${(date.getMonth() + 1)
                                    .toString()
                                    .padStart(2, '0')}/${date.getFullYear()}`;
                        }
                        // Default format if language is not specifically handled
                        return dayjs(rowData.date)
                            .tz(getAppTimezone())
                            .locale(dateConfig.locale)
                            .format('DD.MM.YYYY');
                    }
                    return '';
                },
            },
            {
                title: t('reports.workingHoursReport.firstEntryTime'),
                field: 'first_entry_time',
                filtering: false,
                editable: 'never',
                type: 'datetime',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                render: rowData => {
                    if (
                        rowData.first_entry_time &&
                        rowData.first_entry_time !== 'null' &&
                        rowData.first_entry_time !== 'empty'
                    ) {
                        if (i18n.language === 'bg') {
                            // Custom formatting for Bulgarian dates with Cyrillic characters
                            const date = toTzDate(rowData.first_entry_time);
                            return `${date
                                .getDate()
                                .toString()
                                .padStart(2, '0')}.${(date.getMonth() + 1)
                                    .toString()
                                    .padStart(
                                        2,
                                        '0'
                                    )}.${date.getFullYear()} г., ${date
                                        .getHours()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, '0')}:${date
                                                .getSeconds()
                                                .toString()
                                                .padStart(2, '0')} ч.`;
                        }
                        return dayjs(rowData.first_entry_time)
                            .tz(getAppTimezone())
                            .locale(dateConfig.locale)
                            .format(dateConfig.format);
                    }
                    return '';
                },
            },
            {
                title: t('reports.workingHoursReport.lastExitTime'),
                filtering: false,
                field: 'last_exit_time',
                editable: 'never',
                type: 'datetime',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                render: rowData => {
                    if (
                        rowData.last_exit_time &&
                        rowData.last_exit_time !== 'null' &&
                        rowData.last_exit_time !== 'empty'
                    ) {
                        if (i18n.language === 'bg') {
                            // Custom formatting for Bulgarian dates with Cyrillic characters
                            const date = toTzDate(rowData.last_exit_time);
                            return `${date
                                .getDate()
                                .toString()
                                .padStart(2, '0')}.${(date.getMonth() + 1)
                                    .toString()
                                    .padStart(
                                        2,
                                        '0'
                                    )}.${date.getFullYear()} г., ${date
                                        .getHours()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, '0')}:${date
                                                .getSeconds()
                                                .toString()
                                                .padStart(2, '0')} ч.`;
                        }
                        return dayjs(rowData.last_exit_time)
                            .tz(getAppTimezone())
                            .locale(dateConfig.locale)
                            .format(dateConfig.format);
                    }
                    return '';
                },
            },
            {
                title: t('reports.workingHoursReport.dayShiftHours'),
                field: 'day_shift_hours',
                cellStyle: { whiteSpace: 'nowrap' },
            },
            // {
            //     title: 'Изработено време - дневна (часове)',
            //     field: 'day_shift_hours',
            //     cellStyle: { whiteSpace: 'nowrap' },
            // },
            {
                title: t('reports.workingHoursReport.nightShiftHours'),
                field: 'night_shift_hours',
                cellStyle: { whiteSpace: 'nowrap' },
            },
            // {
            //     title: 'Изработено време - нощна (часове)',
            //     field: 'night_shift_hours',
            //     cellStyle: { whiteSpace: 'nowrap' },
            // },
            {
                title: t('reports.workingHoursReport.restHours'),
                field: 'rest_period',
                cellStyle: { whiteSpace: 'nowrap' },
            },
            {
                title: t('reports.workingHoursReport.totalHours'),
                field: 'total_worked_time_minus_rest',
                cellStyle: { whiteSpace: 'nowrap' },
            },
            // {
            //     title: t('reports.workingHoursReport.problem'),
            //     field: 'problem',
            //     cellStyle: { whiteSpace: 'nowrap' },
            // },
        ]);
    }, [t, i18n.language, dateConfig, departmentsOptions]);

    // ! Format the Date and time columns for export
    const formatDate = inputTime => {
        if (inputTime && inputTime !== 'null' && inputTime !== 'empty') {
            const date = toTzDate(inputTime);
            return dateConfig.dateFormatExport(date);
        }
        return 'empty';
    };

    // ! Format only date part (without time) for exports
    const formatDateOnly = inputDate => {
        if (inputDate && inputDate !== 'null' && inputDate !== 'empty') {
            const date = toTzDate(inputDate);
            if (i18n.language === 'bg') {
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}.${(date.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}.${date.getFullYear()} г.`;
            } else if (i18n.language === 'en') {
                return `${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}/${date
                        .getDate()
                        .toString()
                        .padStart(2, '0')}/${date.getFullYear()}`;
            } else if (i18n.language === 'es') {
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${(date.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}/${date.getFullYear()}`;
            }
            // Default format
            return `${date
                .getDate()
                .toString()
                .padStart(2, '0')}.${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}.${date.getFullYear()}`;
        }
        return 'empty';
    };

    // ! Header mapping for exported column titles
    const headerMapping = {};
    columns.forEach(col => {
        headerMapping[col.field] = col.title;
    });

    const mapHeaders = row => {
        let newRow = {};
        for (let key in row) {
            if (headerMapping[key]) {
                newRow[headerMapping[key]] = row[key];
            } else {
                newRow[key] = row[key];
            }
        }
        return newRow;
    };

    // ! Format the name for the file
    const formatDateForFilename = inputDate => {
        const date = new Date(inputDate);
        return `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${date
                .getDate()
                .toString()
                .padStart(2, '0')}`;
    };

    const formattedStartDate = formatDateForFilename(selectedStartDate);
    const formattedEndDate = formatDateForFilename(selectedEndDate);

    // ! Export to XLS Function
    const tableRef = React.createRef();
    const exportTable = () => {
        // Get the filtered data from the table
        const currentTableData = getMaterialTableExportData(tableRef);

        // Transform the data
        const transformedData = currentTableData.map(row => {
            const hours = row.worked_hours || 0; // Use 0 if worked_hours is null or undefined
            const mins = row.mins || 0; // Use 0 if mins is null or undefined

            // Check for << and >> conditions for first_time and last_time
            const firstTimeValue =
                row.first_time === '<<'
                    ? 'липсва вход'
                    : formatDate(row.first_time);
            const lastTimeValue =
                row.last_time === '>>'
                    ? 'липсва изход'
                    : formatDate(row.last_time);

            return {
                ...row,
                date: formatDateOnly(row.date),
                time: formatDate(row.time),
                first_entry_time: formatDate(row.first_entry_time),
                last_exit_time: formatDate(row.last_exit_time),
                worked_hours: `${hours} часа и ${mins} минути`,
            };
        });

        // Only include displayed columns in the export
        const exportedData = transformedData.map(row => {
            let newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        // Export the data
        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'xls', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `Working_Hours_${formattedStartDate} - ${formattedEndDate}.xls`
        );
    };

    const exportTableCsv = () => {
        // Get the filtered data from the table
        const currentTableData = getMaterialTableExportData(tableRef);

        // Transform the data
        const transformedData = currentTableData.map(row => {
            const hours = row.worked_hours || 0; // Use 0 if worked_hours is null or undefined
            const mins = row.mins || 0; // Use 0 if mins is null or undefined

            // Check for << and >> conditions for first_time and last_time
            const firstTimeValue =
                row.first_entry_time === '<<'
                    ? 'липсва вход'
                    : formatDate(row.first_entry_time);
            const lastTimeValue =
                row.last_exit_time === '>>'
                    ? 'липсва изход'
                    : formatDate(row.last_exit_time);

            return {
                ...row,
                date: formatDateOnly(row.date),
                time: formatDate(row.time),
                first_entry_time: formatDate(row.first_entry_time),
                last_exit_time: formatDate(row.last_exit_time),
                worked_hours: `${hours} часа и ${mins} минути`,
            };
        });

        // Only include displayed columns in the export
        const exportedData = transformedData.map(row => {
            const newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        // Export the data
        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'csv', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `Working_Hours_${formattedStartDate} - ${formattedEndDate}.csv`
        );
    };

    return (
        <div>
            <Paper>
                <Grid
                    container
                    spacing={1}
                    className={classes.absoluteContainer}
                    style={{ paddingLeft: '16px' }}
                >
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedStartDate"
                                label={t('reports.common.startDate')}
                                ampm={false}
                                value={selectedStartDate}
                                onChange={date =>
                                    handleStartDateChange(
                                        normalizeToolbarStartDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedEndDate"
                                label={t('reports.common.endDate')}
                                value={selectedEndDate}
                                onChange={date =>
                                    handleEndDateChange(
                                        normalizeToolbarEndDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={6}>
                        {/*<Button*/}
                        {/*    type="submit"*/}
                        {/*    variant="contained"*/}
                        {/*    color="primary"*/}
                        {/*    className={classes.button}*/}
                        {/*    onClick={retrieveFilteredDay}*/}
                        {/*>*/}
                        {/*    Филтрирай*/}
                        {/*</Button>*/}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={retrieveFilteredDay}
                            disabled={isLoading} // Disable button when loading
                        >
                            {isLoading
                                ? t('reports.common.loading')
                                : t('reports.common.filter')}
                        </Button>
                    </Grid>
                </Grid>
                <MaterialTable
                    title=""
                    columns={[
                        {
                            title: t('reports.workingHoursReport.employeeID'),
                            field: 'user_id',
                            editable: 'never',
                        },
                        {
                            title: t('reports.workingHoursReport.employeeName'),
                            field: 'user_name',
                            editable: 'never',
                        },
                        {
                            title: t(
                                'reports.workingHoursReport.deviceLocation'
                            ),
                            field: 'location_name',
                            editable: 'never',
                        },
                        {
                            title: t('reports.workingHoursReport.department'),
                            field: 'sub_loc_name',
                            lookup: departmentsOptions,
                            editable: 'never',
                        },
                        {
                            title: t('reports.workingHoursReport.date'),
                            field: 'date',
                            type: 'date',
                            dateSetting: {
                                locale: dateConfig.locale,
                                format: dateConfig.format,
                                hour12: false,
                            },
                            editable: 'never',
                            filtering: false,
                            render: rowData => {
                                if (
                                    rowData.date &&
                                    rowData.date !== 'null' &&
                                    rowData.date !== 'empty'
                                ) {
                                    if (i18n.language === 'bg') {
                                        // Custom formatting for Bulgarian dates with Cyrillic characters
                                        const date = toTzDate(rowData.date);
                                        return `${date
                                            .getDate()
                                            .toString()
                                            .padStart(2, '0')}.${(
                                                date.getMonth() + 1
                                            )
                                                .toString()
                                                .padStart(
                                                    2,
                                                    '0'
                                                )}.${date.getFullYear()} г.`;
                                    } else if (i18n.language === 'en') {
                                        // US format: MM/DD/YYYY
                                        const date = toTzDate(rowData.date);
                                        return `${(date.getMonth() + 1)
                                            .toString()
                                            .padStart(2, '0')}/${date
                                                .getDate()
                                                .toString()
                                                .padStart(
                                                    2,
                                                    '0'
                                                )}/${date.getFullYear()}`;
                                    } else if (i18n.language === 'es') {
                                        // Spanish format: DD/MM/YYYY
                                        const date = toTzDate(rowData.date);
                                        return `${date
                                            .getDate()
                                            .toString()
                                            .padStart(2, '0')}/${(
                                                date.getMonth() + 1
                                            )
                                                .toString()
                                                .padStart(
                                                    2,
                                                    '0'
                                                )}/${date.getFullYear()}`;
                                    }
                                    // Default format if language is not specifically handled
                                    return dayjs(rowData.date)
                                        .tz(getAppTimezone())
                                        .locale(dateConfig.locale)
                                        .format('DD.MM.YYYY');
                                }
                                return '';
                            },
                        },
                        {
                            title: t(
                                'reports.workingHoursReport.firstEntryTime'
                            ),
                            field: 'first_entry_time',
                            filtering: false,
                            editable: 'never',
                            type: 'datetime',
                            dateSetting: {
                                locale: dateConfig.locale,
                                format: dateConfig.format,
                                hour12: false,
                            },
                            render: rowData => rowData.first_entry_time_display || '',
                        },
                        {
                            title: t('reports.workingHoursReport.lastExitTime'),
                            filtering: false,
                            field: 'last_exit_time',
                            editable: 'never',
                            type: 'datetime',
                            dateSetting: {
                                locale: dateConfig.locale,
                                format: dateConfig.format,
                                hour12: false,
                            },
                            render: rowData => rowData.last_exit_time_display || '',
                        },
                        {
                            title: t(
                                'reports.workingHoursReport.dayShiftHours'
                            ),
                            field: 'day_shift_hours',
                            cellStyle: { whiteSpace: 'nowrap' },
                            filtering: false,
                        },
                        // {
                        //     title: 'Изработено време - дневна (часове)',
                        //     field: 'day_shift_hours',
                        //     cellStyle: { whiteSpace: 'nowrap' },
                        // },
                        {
                            title: t(
                                'reports.workingHoursReport.nightShiftHours'
                            ),
                            field: 'night_shift_hours',
                            cellStyle: { whiteSpace: 'nowrap' },
                            filtering: false,
                        },
                        // {
                        //     title: 'Изработено време - нощна (часове)',
                        //     field: 'night_shift_hours',
                        //     cellStyle: { whiteSpace: 'nowrap' },
                        // },
                        {
                            title: t('reports.workingHoursReport.restHours'),
                            field: 'rest_period',
                            cellStyle: { whiteSpace: 'nowrap' },
                            filtering: false,
                        },
                        {
                            title: t('reports.workingHoursReport.totalHours'),
                            field: 'total_worked_time_minus_rest',
                            cellStyle: { whiteSpace: 'nowrap' },
                            filtering: false,
                        },
                        // {
                        //     title: t('reports.workingHoursReport.problem'),
                        //     field: 'problem',
                        //     filtering: false,
                        //     cellStyle: { whiteSpace: 'nowrap' },
                        // },
                    ]}
                    data={filteredDay || iserror}
                    tableRef={tableRef}
                    isLoading={isLoading}
                    style={{
                        borderRadius: '0',
                        boxShadow: 'none',
                    }}
                    components={{
                        components,
                        Body: props =>
                            !dataFetched ? (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        margin: '15% auto',
                                        position: 'absolute',
                                        textAlign: 'center',
                                    }}
                                >
                                    {t(
                                        'reports.workingHoursReport.selectPeriod'
                                    )}
                                </div>
                            ) : (
                                <MTableBody {...props} />
                            ),
                    }}
                    actions={[
                        ...(!isAnalyzer
                            ? [
                                {
                                    icon: () => <DownloadOutlinedIcon />,
                                    tooltip: 'Export to CSV',
                                    isFreeAction: true,
                                    onClick: exportTableCsv,
                                },
                                {
                                    icon: () => <DescriptionIcon />,
                                    tooltip: 'Export to Excel',
                                    isFreeAction: true,
                                    onClick: exportTable,
                                },
                            ]
                            : []),
                    ]}
                    options={{
                        loadingType: 'linear',
                        maxBodyHeight: '67vh',
                        minBodyHeight: '67vh',
                        showTitle: true,
                        sorting: true,
                        actionsColumnIndex: -1,
                        filtering: true,
                        pageSize: 100,
                        pageSizeOptions: [50, 100, 200, 300, 400, 500],
                        search: false,
                        toolbar: true,
                    }}
                    onSelectionChange={rows => {
                        setSelectedRows(rows);
                    }}
                    localization={{
                        pagination: {
                            labelDisplayedRows:
                                '{from}-{to} ' +
                                t('reports.common.of') +
                                ' {count}',
                            labelRowsPerPage: t('reports.common.rowsPerPage'),
                            labelRowsSelect: t('reports.common.rowsSelect'),
                        },
                        header: {
                            actions: t('reports.common.actions'),
                        },
                        body: {
                            emptyDataSourceMessage: t(
                                'reports.common.emptyDataSourceMessage'
                            ),
                            editTooltip: t('reports.common.edit'),
                            deleteTooltip: t('reports.common.delete'),
                            filterRow: {
                                filterTooltip: t('reports.common.filter'),
                            },
                            editRow: {
                                deleteText: t(
                                    'reports.common.deleteConfirmation'
                                ),
                                cancelTooltip: t('reports.common.cancel'),
                                saveTooltip: t('reports.common.save'),
                            },
                        },
                        toolbar: {
                            exportTitle: t('reports.common.export'),
                            exportName: t('reports.common.exportCSV'),
                            searchTooltip: t('reports.common.search'),
                            searchPlaceholder: t(
                                'reports.common.searchPlaceholder'
                            ),
                            nRowsSelected: t('reports.common.rowsSelected', {
                                count: selectedRows.length,
                            }),
                        },
                    }}
                />
            </Paper>
        </div>
    );
};

export default WorkingHoursTable;
